import { Badge } from '../ui/Badge';
import type { CandidateRecord, PredictorDiagnostics } from '../../types/market';

interface EagleEyeHeroProps {
  freshness: string;
  warmupProgress: number;
  marketCount: number;
  diagnostics: PredictorDiagnostics;
  predictorRows: CandidateRecord[];
  monitoringRows: CandidateRecord[];
}

export function EagleEyeHero({
  freshness,
  warmupProgress,
  marketCount,
  diagnostics,
  predictorRows,
  monitoringRows,
}: EagleEyeHeroProps): React.JSX.Element {
  const longCount = predictorRows.filter((r) => r.direction === 'long').length;
  const shortCount = predictorRows.filter((r) => r.direction === 'short').length;
  const totalPromoted = predictorRows.length;
  const totalMonitoring = monitoringRows.length;

  return (
    <section className="eagle-eye-hero" aria-label="Eagle Eye radar overview">
      <div className="hero-head">
        <div className="hero-title-group">
          <div className="hero-radar-badge">
            <span className="radar-ping-ring" />
            <span className="radar-ping-dot" />
            <span className="radar-text">EAGLE EYE RADAR</span>
          </div>
          <h1 className="hero-title">Predictor Intelligence</h1>
          <p className="hero-subtitle">
            Continuous deep-validated pattern discovery across {marketCount || 100}+ spot & futures markets
          </p>
        </div>

        <div className="hero-status-pill-group">
          <Badge tone={freshness === 'live' ? 'positive' : 'info'}>
            <span className="badge-pulse-dot" />
            {freshness === 'live' ? 'Live Stream' : freshness}
          </Badge>
          <Badge tone={diagnostics.externalVenueIssues > 0 ? 'warning' : 'positive'}>
            {diagnostics.externalVenuesAvailable}/2 Venues Active
          </Badge>
        </div>
      </div>

      <div className="hero-kpi-grid">
        <div className="hero-kpi-card hero-kpi-highlight">
          <div className="hero-kpi-label">Promoted Candidates</div>
          <div className="hero-kpi-value">{totalPromoted}</div>
          <div className="hero-kpi-sub">
            <span className="text-positive">{longCount} Long</span> · <span className="text-negative">{shortCount} Short</span>
          </div>
        </div>

        <div className="hero-kpi-card">
          <div className="hero-kpi-label">Developing Monitoring</div>
          <div className="hero-kpi-value">{totalMonitoring}</div>
          <div className="hero-kpi-sub">
            {diagnostics.neutralObservations} neutral · {diagnostics.deepQueued} queued
          </div>
        </div>

        <div className="hero-kpi-card">
          <div className="hero-kpi-label">Radar Baseline</div>
          <div className="hero-kpi-value">{warmupProgress}%</div>
          <div className="hero-progress-bar">
            <div className={`hero-progress-fill${warmupProgress > 0 && warmupProgress < 100 ? ' hero-progress-fill--scanning' : ''}`} style={{ width: `${warmupProgress}%` }} />
          </div>
          <div className="hero-kpi-sub">
            {warmupProgress >= 100
              ? `${diagnostics.baselineReadyMarkets}/${diagnostics.eligibleMarkets} markets ready`
              : warmupProgress > 0
                ? `Scanning ${diagnostics.baselineReadyMarkets}/${diagnostics.eligibleMarkets} markets`
                : `${diagnostics.eligibleMarkets} markets discovered · warming up`}
          </div>
        </div>

        <div className="hero-kpi-card">
          <div className="hero-kpi-label">Deep Validation</div>
          <div className="hero-kpi-value">{diagnostics.deepComplete}</div>
          <div className="hero-kpi-sub">
            {diagnostics.deepPartial} partial · {diagnostics.deepFailed} failed
          </div>
        </div>
      </div>
    </section>
  );
}
